import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const MARGIN = { top: 40, right: 120, bottom: 30, left: 60 };
const ROW_HEIGHT = 28;
const SVG_WIDTH = 700;

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: '16px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '0',
  },
  summaryRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
  },
  summaryLabel: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#374151',
    whiteSpace: 'nowrap',
  },
  progressTrack: {
    flex: 1,
    height: '8px',
    background: '#f3f4f6',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.3s ease',
  },
  benchmarkLabel: {
    fontSize: '12px',
    color: '#6b7280',
    textAlign: 'right',
    marginBottom: '8px',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

function getBarColor(deviation, maxAbsDeviation) {
  if (maxAbsDeviation === 0) return '#9ca3af';
  const intensity = Math.min(Math.abs(deviation) / maxAbsDeviation, 1);
  if (deviation > 0) {
    // Red for above benchmark — stronger red for larger deviations
    const r = Math.round(220 + intensity * 35);
    const g = Math.round(160 - intensity * 122);
    const b = Math.round(160 - intensity * 122);
    return `rgb(${Math.min(r, 255)}, ${Math.max(g, 38)}, ${Math.max(b, 38)})`;
  } else {
    // Green for below benchmark — stronger green for larger deviations
    const r = Math.round(160 - intensity * 138);
    const g = Math.round(200 + intensity * 30);
    const b = Math.round(160 - intensity * 96);
    return `rgb(${Math.max(r, 22)}, ${Math.min(g, 230)}, ${Math.max(b, 64)})`;
  }
}

export default function PriceTargetChart({ data }) {
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const sortedStates = useMemo(() => {
    if (!data || !data.states || data.states.length === 0) return [];
    return [...data.states].sort((a, b) => b.deviation - a.deviation);
  }, [data]);

  const benchmark = useMemo(() => {
    if (!data || !data.states || data.states.length === 0) return null;
    const first = data.states[0];
    if (first.benchmark != null) return first.benchmark;
    return first.currentPrice - first.deviation;
  }, [data]);

  useEffect(() => {
    if (!sortedStates || sortedStates.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const height = sortedStates.length * ROW_HEIGHT + MARGIN.top + MARGIN.bottom;
    const innerW = SVG_WIDTH - MARGIN.left - MARGIN.right;
    const innerH = sortedStates.length * ROW_HEIGHT;

    svg
      .attr('viewBox', `0 0 ${SVG_WIDTH} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('width', '100%')
      .style('height', 'auto');

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    // Scales
    const maxAbsDev = d3.max(sortedStates, (d) => Math.abs(d.deviation)) || 1;
    const xPadding = maxAbsDev * 0.15;

    const x = d3.scaleLinear()
      .domain([-(maxAbsDev + xPadding), maxAbsDev + xPadding])
      .range([0, innerW]);

    const y = d3.scaleBand()
      .domain(sortedStates.map((d) => d.state))
      .range([0, innerH])
      .padding(0.25);

    const zeroX = x(0);

    // Light vertical grid lines
    const ticks = x.ticks(8);
    g.selectAll('.grid-v')
      .data(ticks.filter((t) => t !== 0))
      .enter()
      .append('line')
      .attr('x1', (d) => x(d))
      .attr('x2', (d) => x(d))
      .attr('y1', -4)
      .attr('y2', innerH)
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Center line at 0 (benchmark)
    g.append('line')
      .attr('x1', zeroX)
      .attr('x2', zeroX)
      .attr('y1', -4)
      .attr('y2', innerH)
      .attr('stroke', '#374151')
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '4,3');

    // "Benchmark" label at top of center line
    g.append('text')
      .attr('x', zeroX)
      .attr('y', -10)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('font-weight', 500)
      .text('Benchmark');

    // Draw deviation bars
    sortedStates.forEach((d) => {
      const barY = y(d.state);
      const barH = y.bandwidth();
      const cy = barY + barH / 2;
      const devX = x(d.deviation);
      const barStart = d.deviation >= 0 ? zeroX : devX;
      const barWidth = Math.abs(devX - zeroX);
      const color = getBarColor(d.deviation, maxAbsDev);

      const rowGroup = g.append('g')
        .attr('class', 'deviation-row')
        .style('cursor', 'pointer');

      // Invisible hit area
      rowGroup.append('rect')
        .attr('x', 0)
        .attr('y', barY)
        .attr('width', innerW)
        .attr('height', barH)
        .attr('fill', 'transparent');

      // Deviation bar
      rowGroup.append('rect')
        .attr('x', barStart)
        .attr('y', barY + 2)
        .attr('width', Math.max(barWidth, 1))
        .attr('height', barH - 4)
        .attr('fill', color)
        .attr('rx', 2);

      // State label on the left
      g.append('text')
        .attr('x', -8)
        .attr('y', cy)
        .attr('dy', '0.35em')
        .attr('text-anchor', 'end')
        .attr('font-size', '11px')
        .attr('font-weight', 500)
        .attr('fill', '#374151')
        .text(d.state);

      // Dollar deviation label at bar end
      const labelX = d.deviation >= 0 ? devX + 4 : devX - 4;
      const labelAnchor = d.deviation >= 0 ? 'start' : 'end';
      const sign = d.deviation >= 0 ? '+' : '';

      g.append('text')
        .attr('x', labelX)
        .attr('y', cy)
        .attr('dy', '0.35em')
        .attr('text-anchor', labelAnchor)
        .attr('font-size', '10px')
        .attr('font-weight', 600)
        .attr('fill', d.deviation >= 0 ? '#dc2626' : '#16a34a')
        .text(`${sign}$${d.deviation.toFixed(2)} (${sign}${d.deviationPercent.toFixed(1)}%)`);

      // Hover interactions
      rowGroup
        .on('mouseenter', (event) => {
          rowGroup.select('rect:nth-child(2)')
            .attr('y', barY + 1)
            .attr('height', barH - 2)
            .attr('opacity', 0.85);

          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            state: d.state,
            currentPrice: d.currentPrice,
            benchmark: d.benchmark,
            deviation: d.deviation,
            deviationPercent: d.deviationPercent,
            status: d.status,
          });
        })
        .on('mousemove', (event) => {
          setTooltip((prev) =>
            prev
              ? {
                  ...prev,
                  x: event.clientX + 12,
                  y: event.clientY - 10,
                }
              : prev
          );
        })
        .on('mouseleave', () => {
          rowGroup.select('rect:nth-child(2)')
            .attr('y', barY + 2)
            .attr('height', barH - 4)
            .attr('opacity', 1);

          setTooltip(null);
        });
    });

    // Bottom axis with dollar labels
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(
        d3.axisBottom(x)
          .ticks(8)
          .tickSize(0)
          .tickFormat((d) => {
            if (d === 0) return '$0';
            const sign = d > 0 ? '+' : '';
            return `${sign}$${d.toFixed(0)}`;
          })
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    return () => {
      svg.selectAll('*').remove();
    };
  }, [sortedStates]);

  if (!data || !data.states || data.states.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>Price vs National Benchmark</div>
          <div style={styles.subtitle}>State-level deviation from the national average price</div>
        </div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No data available
        </div>
      </div>
    );
  }

  const withinTarget = data.withinTarget || 0;
  const totalStates = data.totalStates || sortedStates.length;
  const progressPercent = totalStates > 0 ? (withinTarget / totalStates) * 100 : 0;
  const progressColor = progressPercent >= 75 ? '#16a34a' : progressPercent >= 50 ? '#f59e0b' : '#dc2626';

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>Price vs National Benchmark</div>
        <div style={styles.subtitle}>State-level deviation from the national average price</div>
      </div>

      <div style={styles.summaryRow}>
        <span style={styles.summaryLabel}>
          {withinTarget} of {totalStates} states within target range
        </span>
        <div style={styles.progressTrack}>
          <div
            style={{
              ...styles.progressFill,
              width: `${progressPercent}%`,
              background: progressColor,
            }}
          />
        </div>
        <span style={{ fontSize: '12px', fontWeight: 600, color: progressColor, whiteSpace: 'nowrap' }}>
          {progressPercent.toFixed(0)}%
        </span>
      </div>

      {benchmark != null && (
        <div style={styles.benchmarkLabel}>
          National Avg: ${benchmark.toFixed(2)}
        </div>
      )}

      <div style={styles.container}>
        <svg ref={svgRef} />
      </div>

      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {tooltip.state}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Current Price</span>
            <span style={{ fontWeight: 600 }}>${tooltip.currentPrice.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Benchmark</span>
            <span style={{ fontWeight: 600 }}>${tooltip.benchmark.toFixed(2)}</span>
          </div>
          <div style={{ borderTop: '1px solid #f3f4f6', marginTop: '4px', paddingTop: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
              <span style={{ color: '#6b7280' }}>Deviation</span>
              <span
                style={{
                  fontWeight: 600,
                  color: tooltip.deviation >= 0 ? '#dc2626' : '#16a34a',
                }}
              >
                {tooltip.deviation >= 0 ? '+' : ''}${tooltip.deviation.toFixed(2)}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
              <span style={{ color: '#6b7280' }}>Deviation %</span>
              <span
                style={{
                  fontWeight: 600,
                  color: tooltip.deviation >= 0 ? '#dc2626' : '#16a34a',
                }}
              >
                {tooltip.deviationPercent >= 0 ? '+' : ''}{tooltip.deviationPercent.toFixed(1)}%
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
              <span style={{ color: '#6b7280' }}>Status</span>
              <span
                style={{
                  fontWeight: 600,
                  color:
                    tooltip.status === 'above'
                      ? '#dc2626'
                      : tooltip.status === 'below'
                      ? '#16a34a'
                      : '#6b7280',
                }}
              >
                {tooltip.status === 'above'
                  ? 'Above Avg'
                  : tooltip.status === 'below'
                  ? 'Below Avg'
                  : 'At Target'}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
